import React, { useState, useEffect } from 'react';
import './ScheduleInspectionOverlay.css';
import { CONTACT_API_URL, hasContactApi } from './api';
import emailjs from '@emailjs/browser';

const ScheduleInspectionOverlay = ({ isOpen, onClose, property }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Reset form when modal opens
      setFormData({
        name: '',
        email: '',
        phone: '',
        preferredDate: '',
        preferredTime: '',
        message: '',
      });
      setShowSuccess(false);
      setIsSubmitting(false);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const propertyAddress = property ? `${property.address}, ${property.suburb}` : 'Unknown Property';
    
    // Construct a clear subject and message body for the inspection request
    const emailSubject = `Inspection Request for ${propertyAddress}`;
    const detailedMessage = `
      PROPERTY: ${propertyAddress}
      
      PREFERRED DATE: ${formData.preferredDate}
      PREFERRED TIME: ${formData.preferredTime}
      
      ADDITIONAL MESSAGE:
      ${formData.message}
    `;

    // 1. Try Custom API
    if (hasContactApi()) {
      try {
        const res = await fetch(CONTACT_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            name: formData.name, 
            email: formData.email, 
            phone: formData.phone, 
            subject: emailSubject, 
            message: detailedMessage 
          }),
        });
        
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || `Request failed: ${res.status}`);
        }

        handleSuccess();
      } catch (err) {
        console.error('API inspection request failed:', err);
        setIsSubmitting(false); 
      }
      return;
    }

    // 2. Fallback to EmailJS
    try {
      // EmailJS configuration
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'your_service_id';
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'your_template_id';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'your_public_key';
      
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        subject: emailSubject,
        message: detailedMessage,
        property_address: propertyAddress,
        to_email: 'abhi@auzlandre.com.au'
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      handleSuccess();

    } catch (err) {
      console.error('EmailJS inspection request failed:', err);
      alert('Failed to submit request. Please try again or contact us directly.');
      setIsSubmitting(false);
    }
  };

  const handleSuccess = () => {
      setIsSubmitting(false);
      setShowSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="offer-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          &times;
        </button>
        
        <div className="offer-header">
          <h3>Schedule a Private Inspection</h3>
          {property && <p>For: {property.address}, {property.suburb}</p>}
        </div>

        {showSuccess ? (
            <div className="offer-success">
                <h4>Thank You!</h4>
                <p>Your inspection request has been submitted successfully.</p>
            </div>
        ) : (
            <form className="offer-form" onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your Name"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Your Phone Number"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="preferredDate">Preferred Date *</label>
                    <input
                        type="date"
                        id="preferredDate"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="preferredTime">Preferred Time</label>
                    <input
                        type="time"
                        id="preferredTime"
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleChange}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="4"
                        placeholder="Any additional notes..."
                    ></textarea>
                </div>
                <div className="form-actions">
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Request Inspection'}
                    </button>
                </div>
            </form>
        )}
      </div>
    </div>
  );
};

export default ScheduleInspectionOverlay;
